import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const ChangeOrders = () => {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [changeOrders, setChangeOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (token) {
      fetchChangeOrders();
    }
  }, [statusFilter, token]);

  const fetchChangeOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await fetch(`${API_URL}/change-orders?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch change orders');
      const data = await response.json();
      setChangeOrders(data.changeOrders || []);
    } catch (error) {
      console.error('Error fetching change orders:', error);
      toast.error('Failed to load change orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#fef3c7', color: '#92400e', icon: ClockIcon },
      approved: { bg: '#d1fae5', color: '#065f46', icon: CheckCircleIcon },
      rejected: { bg: '#fee2e2', color: '#991b1b', icon: XCircleIcon }
    };

    const style = styles[status] || styles.pending;
    const Icon = style.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        background: style.bg,
        color: style.color
      }}>
        <Icon style={{ width: 14, height: 14 }} />
        {status.toUpperCase()}
      </span>
    );
  };

  const filteredChangeOrders = changeOrders.filter(co =>
    co.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    co.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    co.service_call_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusCounts = {
    all: changeOrders.length,
    pending: changeOrders.filter(co => co.status === 'pending').length,
    approved: changeOrders.filter(co => co.status === 'approved').length,
    rejected: changeOrders.filter(co => co.status === 'rejected').length
  };

  return (
    <div style={{ padding: '24px', background: '#f9fafb', minHeight: 'calc(100vh - 48px)' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '32px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>Change Orders</h1>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Track and manage project scope changes</p>
        </div>
        <button
          onClick={() => navigate('/change-orders/new')}
          style={{
            padding: '12px 20px',
            background: '#2B7FE0',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'background 0.2s'
          }}
          onMouseOver={e => e.target.style.background = '#2563eb'}
          onMouseOut={e => e.target.style.background = '#2B7FE0'}
        >
          <PlusIcon style={{ width: 18, height: 18 }} /> New Change Order
        </button>
      </div>

      {/* Status Filter Tabs */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {[
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' }
        ].map(status => (
          <button
            key={status.value}
            onClick={() => setStatusFilter(status.value)}
            style={{
              padding: '8px 16px',
              border: statusFilter === status.value ? 'none' : '1px solid #d1d5db',
              borderRadius: '6px',
              background: statusFilter === status.value ? '#2B7FE0' : 'white',
              color: statusFilter === status.value ? 'white' : '#6b7280',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {status.label} ({statusCounts[status.value]})
          </button>
        ))}
      </div>

      {/* Search and Content */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {/* Search Bar */}
        <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
          <input
            type="text"
            placeholder="🔍 Search change orders by title, client, or service call..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Change Orders Table */}
        <div style={{ padding: '20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              Loading change orders...
            </div>
          ) : filteredChangeOrders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              {searchTerm ? 'No change orders match your search' : 'No change orders found'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Title</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Service Call</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Client</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Price Change</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Requested</th>
                  <th style={{ textAlign: 'center', padding: '12px', color: '#6b7280', fontWeight: '600', fontSize: '14px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredChangeOrders.map(co => (
                  <tr key={co.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '16px 12px' }}>
                      <div style={{ fontWeight: '500', color: '#1f2937' }}>{co.title}</div>
                    </td>
                    <td style={{ padding: '16px 12px', color: '#6b7280', fontSize: '14px' }}>
                      {co.service_call_title || '—'}
                    </td>
                    <td style={{ padding: '16px 12px', color: '#6b7280', fontSize: '14px' }}>
                      {co.client_name || '—'}
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      {co.price_difference !== null ? (
                        <span style={{
                          fontWeight: '500',
                          color: co.price_difference >= 0 ? '#dc2626' : '#059669'
                        }}>
                          {co.price_difference >= 0 ? '+' : ''}
                          ${parseFloat(co.price_difference).toLocaleString()}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '16px 12px' }}>
                      {getStatusBadge(co.status)}
                    </td>
                    <td style={{ padding: '16px 12px', color: '#6b7280', fontSize: '14px' }}>
                      {new Date(co.requested_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <button
                        onClick={() => navigate(`/change-orders/${co.id}`)}
                        style={{
                          padding: '8px',
                          background: 'none',
                          color: '#2B7FE0',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="View details"
                      >
                        <EyeIcon style={{ width: 18, height: 18 }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeOrders;